import * as React from 'react';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { AmorgAdminPageProvider } from '../AmorgAdminPageContext';
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { useNavigate, useParams } from 'react-router-dom';
import { getActiveUser, getAllSites, getOrganizations, getSiteById, getSiteUsers, updateSite } from '../../../api/adminClientApi';
import { FormField } from '../../../designSystem/formField/FormField';
import { TextInputWithSave } from '../../../designSystem/inputs/TextInputWithSave';
import { H3 } from '../../../designSystem/typography/Heading';
import { Checkbox } from '../../../designSystem/inputs/Checkbox';
import { userHelper } from '../../../amorg-common/helpers/userHelper';
import { colors } from '../../../designSystem/colors/color';
import { Icon } from '../../../designSystem/icons/Icons';
import { ImpersonateModalButton } from '../_components/ImpersonateModalButton';
import { txt } from '../../../txt';
import { OnOff } from '../../../designSystem/inputs/OnOff';
import { Em } from '../../../designSystem/typography/Em';
import { PageSection } from '../../../designSystem/PageSection';
import { MessageCard } from '../../../designSystem/Message';
import { SiteView, PublicAmorgUser, AmorgAdminToken, SiteSettings, ApplicationFeatures, CreceaOnlyFeatures, ApplicationFeature, OrganizationListView, SiteFlags, SiteFlag } from '../../../amorg-common';
import { AdminSiteTabs } from '../_components/AdminSiteTabs';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { SafeFlagSelector } from '../../../designSystem/compositeComponents/SafeFlagSelector';
import { SiteScriptModalButton } from './SiteScriptModalButton';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { Button } from '../../../designSystem/button/Button';
import { InfoChip } from '../../../designSystem/Chip';
import { Table } from '../../../designSystem/Table';


export function AmorgAdminSite (): JSX.Element
{
    const { siteId } = useParams<{ siteId: string; }>();
    
    const [loading, setLoading] = React.useState(true);
    // This is used because resetting the loginName on invalid entry may not be picked up as a change
    const [loginNameResetSignal, setLoginNameResetSignal] = React.useState(0);
    
    const [organizations, setOrganizations] = React.useState<OrganizationListView[]>([]);
    const [site, setSite] = React.useState<SiteView | null>(null);
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);

    const navigate = useNavigate();

    React.useEffect(() =>
    {
        const init = async () =>
        {
            const token = await getActiveUser(() => navigate(`/amorgy/login`));
            setActiveUser(token);

            await reloadState();
        };
        init().catch(console.error);
    }, []);

    async function reloadState()
    {
        setLoading(true);

        const site = await getSiteById(siteId || '', () => navigate("/amorgy/login"));
        if (!site)
        {
            throw new Error("Invalid siteId");
        }
        setSite(site);

        const orgs = await getOrganizations(() => navigate("/amorgy/login"));
        setOrganizations(orgs);

        document.title = `Amorgy - Sites - ${site.loginName}`;

        const users = await getSiteUsers(site.id, () => navigate("/amorgy/login"));
        setUsers(users);

        setLoading(false);
    }

    async function handleUpdateSiteSettings (organisationId:string, settings: SiteSettings, loginName: string)
    {
        if (!siteId)
            return;

        setLoading(true);
        const result = await updateSite(siteId, organisationId, settings, loginName, () => navigate('/amorgy/login'));
        if(!result)
        {
            alert('Fejl ved opdatering af siteindstillinger');
        }

        const sites = await getAllSites(() => navigate("/amorgy/login"));
        const updatedSite = sites.find((s) => s.id === siteId);
        if (!updatedSite)
        {
            throw new Error(`Could not find updated site with id ${siteId}`);
        }

        
        setSite(updatedSite);
        setLoginNameResetSignal((current) => current + 1);
        setLoading(false);
    }

    return (
        <>
            {site &&

                <AmorgAdminPageProvider>
                    <LoadingPageOverlay loading={loading} />
                    <SingleColumnLayout
                        noDivider
                        headerContainer={
                            <Flex flexDirection="column" gap={16}>
                                <AdminSiteTabs selectedAdminTab='sites' selectedSiteTab='siteOverview' 
                                    siteId={siteId} 
                                    orgId={site?.organization?.id} />
                            </Flex>
                        }

                        contentContainer={
                            <Flex flexDirection='column' gap={32}>

                                {activeUser &&
                                    <>
                                        <MessageCard variant="success">
                                            <Flex gap={8}>
                                                <Icon name="status_ok" color={colors.icon.success} />
                                                <span>Ingen notifikationer</span>
                                            </Flex>
                                        </MessageCard>

                                        <SettingsSection 
                                            site={site} 
                                            organizations={organizations} 
                                            loginNameResetSignal={loginNameResetSignal}
                                            onUpdateSiteSettings={
                                                (organisationId: string, settings: SiteSettings, loginName: string) => 
                                                    handleUpdateSiteSettings(organisationId, settings, loginName)
                                        } />

                                        <FeaturesSection activeUser={activeUser} site={site} 
                                            onUpdateSiteSettings={
                                                (settings: SiteSettings, loginName: string) =>
                                                    handleUpdateSiteSettings(site.organization?.id || '', settings, loginName)
                                            } 
                                            onUpdated={reloadState}
                                        />

                                        <UserSection users={users} siteId={siteId || ''} />
                                    </>                                
                                }
                                
                            </Flex>
                        } />
                </AmorgAdminPageProvider>

            }      </>
    );
}


function SettingsSection ({ site, organizations, loginNameResetSignal, onUpdateSiteSettings }: 
    { 
        site: SiteView,
        organizations: OrganizationListView[],
        loginNameResetSignal: number,
        onUpdateSiteSettings: (organizationId:string, settings: SiteSettings, loginName: string) => void 
    }): JSX.Element
{
    function handleChangeApiVideoKey ()
    {
        return (value: string) => onUpdateSiteSettings(site.organization?.id || '', { ...site.settings, apiVideoKey: value }, site.loginName);
    }

    return (
        <PageSection
            header={{
                title: 'Siteindstillinger',
                icon: 'navigation_settings',
                defaultCollapsed: false
            }}>
            <Flex flexDirection="column" gap={16}>

                <Flex gap={64}>

                    <FormField
                        style={{ flexGrow: 0 }}
                        label='siteId'
                        inputComponent={<Em>{site.id}</Em>}
                    />

                    <FormField
                        style={{ flexGrow: 0 }}
                        label='Adgang'
                        inputComponent={
                            <OnOff
                                active={site.settings.enabled}
                                onClick={() =>
                                {
                                    onUpdateSiteSettings(site.organization?.id || '', { ...site.settings, enabled: !site.settings.enabled }, site.loginName);
                                }} />
                        }
                    />


                    <Flex flexDirection="column" gap={16} grow="1">

                        <SelectField
                            label="Organisation"
                            options={organizations.sort((a, b) => a.companyInfo.name.localeCompare(b.companyInfo.name)).map(org => ({ text: org.companyInfo.name, value: org.id }))}
                            selectedValue={site.organization?.id || ''}
                            onChange={(value) =>
                            {
                                onUpdateSiteSettings(value, site.settings, site.loginName);
                            }}
                        />

                        <FormField
                            label="Navn ved login"
                            toolTip="Navnet brugere skal anvende ved login"
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={site.loginName}
                                    resetSignal={loginNameResetSignal}
                                    onSave={(value) => onUpdateSiteSettings(site.organization?.id || '', site.settings, value)}
                                />
                            }
                        />

                        <FormField
                            label='Video API key'
                            inputComponent={
                                <TextInputWithSave
                                    type={'text'}
                                    value={site.settings.apiVideoKey}
                                    onSave={handleChangeApiVideoKey()}
                                />
                            }
                        />

                        <SafeFlagSelector allowedFlags={SiteFlags.map(f => f)}
                            colorMode='colorIndex'
                            selectedFlags={site.settings.flags} onChange={(flags) => onUpdateSiteSettings(site.organization?.id || '', { ...site.settings, flags: flags as SiteFlag[] }, site.loginName)} />   

                    </Flex>
                </Flex>
                

                <Flex justifyContent='flex-end'>
                    <ImpersonateModalButton
                        siteId={site.id}
                        siteEnabled={site.settings.enabled} />
                </Flex>
            </Flex>
        </PageSection>
    );
}

function FeaturesSection ({ 
    activeUser, 
    site, 
    onUpdateSiteSettings, 
    onUpdated
}: { 
    activeUser: AmorgAdminToken; 
    site: SiteView; 
    onUpdateSiteSettings: (settings: SiteSettings, loginName: string) => void; 
    onUpdated: () => void; 
}): JSX.Element
{
    
    const isCreceaOrAdmin = activeUser.superAdmin || activeUser.partner === 'Crecea';

    return (
        <PageSection
            header={{
                title: 'Funktioner',
                icon: 'navigation_preferences',
                defaultCollapsed: false
            }}>

            <Flex flexDirection='row' gap={16}>

                <Flex flexDirection='column'>
                    <H3>Basefunktionalitet</H3>
                    {ApplicationFeatures.filter(f => !CreceaOnlyFeatures.includes(f)).map(f =>
                        <Checkbox
                            key={f}
                            text={txt.featureText(f)}
                            value={f}
                            checked={site.settings.features.includes(f)}
                            onChange={(name, checked) =>
                            {
                                const features: ApplicationFeature[] = checked
                                    ? [...site.settings.features, name as ApplicationFeature]
                                    : site.settings.features.filter(f => f !== name);
                                onUpdateSiteSettings({ ...site.settings, features }, site.loginName);
                            }} />
                    )}
                </Flex>

                {isCreceaOrAdmin &&
                    <Flex flexDirection='column'>
                        <H3>Crecea funktionalitet</H3>
                        {CreceaOnlyFeatures.map(f =>
                            <Checkbox
                                key={f}
                                text={txt.featureText(f)}
                                value={f}
                                checked={site.settings.features.includes(f)}
                                onChange={(name, checked) =>
                                {
                                    const features: ApplicationFeature[] = checked
                                        ? [...site.settings.features, name as ApplicationFeature]
                                        : site.settings.features.filter(f => f !== name);
                                    onUpdateSiteSettings({ ...site.settings, features }, site.loginName);
                                }} />
                        )}
                    </Flex>
                }

                <Flex flexDirection='column'>
                    <H3>Valgindstillinger</H3>
                    <Checkbox
                        id="hideSurveyEmailAddresses"
                        text='Skjul email i spørgeskemabesvarelser'
                        value='hideSurveyEmailAddresses'
                        checked={site.settings.hideSurveyEmailAddresses}
                        onChange={(name, checked) =>
                        {
                            onUpdateSiteSettings({ ...site.settings, hideSurveyEmailAddresses: checked }, site.loginName);
                        }} />
                </Flex>
            </Flex>

            <ButtonRow variant='right-aligned'>
                <SiteScriptModalButton siteId={site.id} onUpdated={onUpdated} />
            </ButtonRow>
        </PageSection>
    );
}


type UsersProps = {
    users: PublicAmorgUser[];
    siteId: string;
};
function UserSection (props: UsersProps): JSX.Element
{
    const navigate = useNavigate();

    return (

        <PageSection
            header={{
                title: 'Brugere',
                icon: 'interface_user_team',
                defaultCollapsed: false
            }}>
            <Flex flexDirection="column" gap={16}>
                <Flex justifyContent="flex-end">
                    <Button
                        variant="filled"
                        text="Importér brugere"
                        icon="cloud_upload"
                        onClick={() => navigate(`/amorgy/sites/${props.siteId}/import-users`)}
                    />
                </Flex>

                <Table>
                    <thead>
                        <tr>
                            <th>Navn</th>
                            <th>Rolle</th>
                            <th>Email</th>
                            <th>Rettigheder</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props.users.length === 0 && (
                            <tr>
                                <td colSpan={4} style={{ textAlign: 'center' }}>
                                    Ingen brugere oprettet
                                </td>
                            </tr>
                        )}
                        {props.users.map(u => (
                            <tr key={u.id}>
                                <td style={{ fontSize: '12px' }}>{userHelper.getPublicUserFullName(u)}</td>
                                <td style={{ fontSize: '12px' }}>{txt.getDynamic('PermissionGroupId_' + u.permissionGroup.id)}</td>
                                <td style={{ fontSize: '12px' }}>{u.email}</td>
                                <td>
                                    {u.settings.articleContact && <InfoChip>Lovservice kontakt</InfoChip>}
                                    {u.individualPermissions.includes('ManageIncidents') && <InfoChip>Indberetninger</InfoChip>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Flex>
        </PageSection>
    );
}